import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { getCachedPublicCourseList } from '../../services/courseCatalogCache';
import { CourseListItem, Category } from '../../types/content';
import Input from '../../components/UI/Input';
import CourseFee from '../../components/UI/CourseFee';
import CourseRating from '../../components/content/CourseRating';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import CatalogFilterLayout, { CatalogFilterSelect } from '../../components/content/CatalogFilterLayout';
import { COURSE_IMAGES } from '../../config/courseImages';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroImageFade, heroShell } from '../../content/heroThemes';

const CoursesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(searchParams.get('category') || '');
  const [difficulty, setDifficulty] = useState('');
  const [minRating, setMinRating] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const appliedSearch = search.trim().length >= 3 ? search.trim() : '';
  const filterKey = `${appliedSearch}|${categoryId}|${difficulty}|${minRating}`;
  const previousFilterKey = useRef(filterKey);

  useEffect(() => {
    const fromQuery = searchParams.get('category') || '';
    setCategoryId(fromQuery);
  }, [searchParams]);

  useEffect(() => {
    contentApi.listCategories('Course').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!getCachedPublicCourseList()) setLoading(true);
    contentApi.listCourses({
      search: appliedSearch || undefined,
      categoryId: categoryId || undefined,
      difficulty: difficulty || undefined,
      minRating: minRating || undefined,
      sort: 'catalog',
      pageSize: 50,
    }).then((r) => {
      if (cancelled) return;
      setCourses(r.data.courses || []);
      setError(null);
    }).catch(() => {
      if (!cancelled) setError('Unable to load courses.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [appliedSearch, categoryId, difficulty, minRating]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useLayoutEffect(() => {
    const filtersChanged = previousFilterKey.current !== filterKey;
    previousFilterKey.current = filterKey;
    if (!filtersChanged || !courses.length) return;
    const first = resultsRef.current?.querySelector<HTMLAnchorElement>('a[href^="/courses/"]');
    resultsRef.current?.scrollIntoView({ block: 'start' });
    first?.focus({ preventScroll: true });
  }, [filterKey, courses]);

  const hint = useMemo(() => {
    const typed = search.trim();
    if (typed.length > 0 && typed.length < 3) {
      return 'Type at least 3 characters to search.';
    }
    return null;
  }, [search]);

  return (
    <div>
      <section className={heroShell('courses')}>
        <HeroBackdrop tone="courses" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.35rem] md:py-[1.575rem] lg:py-[1.8rem]">
          <div className="grid lg:grid-cols-2 gap-[1.125rem] lg:gap-[1.8rem] items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">Self-paced · Learn by doing</p>
              <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                Courses that connect topics with hands-on labs
              </h1>
              <p className="mt-4 text-sm sm:text-base text-primary-100/90 leading-relaxed">
                Explore self-paced courses that pair each topic with hands-on labs, so you can apply what you learn as you go.
              </p>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={COURSE_IMAGES.coursesHero}
                  alt="Engineers learning a software engineering course"
                  className="w-full h-[12.6rem] sm:h-[15.3rem] lg:h-[18rem] object-cover object-center"
                />
                <div className={heroImageFade('courses')} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <CatalogFilterLayout
        applied={[
          ...(appliedSearch ? [{ key: 'search', label: `Search: ${appliedSearch}`, onRemove: () => setSearch('') }] : []),
          ...(categoryId ? [{ key: 'category', label: `Category: ${categories.find((c) => String(c.id) === String(categoryId))?.name || categoryId}`, onRemove: () => setCategoryId('') }] : []),
          ...(difficulty ? [{ key: 'difficulty', label: `Difficulty: ${difficulty}`, onRemove: () => setDifficulty('') }] : []),
          ...(minRating ? [{ key: 'rating', label: `${minRating}+ stars`, onRemove: () => setMinRating('') }] : []),
        ]}
        onClearFilters={() => {
          setSearch('');
          setCategoryId('');
          setDifficulty('');
          setMinRating('');
        }}
        results={(
          <div ref={resultsRef} className="scroll-mt-24">
            {loading && <PageLoading label="Loading courses…" />}
            {error && <p className="p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</p>}
            {hint && <p className="mb-3 text-sm text-gray-500">{hint}</p>}
            {!loading && !error && courses.length === 0 && <p className="text-gray-500">No published courses match these filters.</p>}
            {courses.length > 0 && (
              <div className="catalog-panel">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Title</th>
                      <th className="px-4 py-3 font-semibold">Subtitle</th>
                      <th className="px-4 py-3 font-semibold">Category</th>
                      <th className="px-4 py-3 font-semibold">Difficulty</th>
                      <th className="px-4 py-3 font-semibold">Duration</th>
                      <th className="px-4 py-3 font-semibold">Curriculum</th>
                      <th className="px-4 py-3 font-semibold">Fee</th>
                      <th className="px-4 py-3 font-semibold">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {courses.map((course) => (
                      <tr key={course.id} className="hover:bg-primary-50/40">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          <Link to={`/courses/${course.slug}`} className="text-primary-700 hover:underline">
                            {course.title}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs">{course.subtitle || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{course.categoryName || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{course.difficulty}</td>
                        <td className="px-4 py-3 text-gray-700">{course.estimatedDurationHours ? `${course.estimatedDurationHours}+ hrs` : '—'}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {course.moduleCount ?? 0} modules · {course.topicCount ?? 0} topics
                          {course.labCount ? ` · ${course.labCount} labs` : ''}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-semibold">
                          <CourseFee fee={Number(course.price || 0)} discount={Number(course.discountPercent || 0)} />
                        </td>
                        <td className="px-4 py-3">
                          <CourseRating average={course.averageRating} count={course.ratingCount} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      >
        <Input id="search" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search courses (3+ characters)" />
        <CatalogFilterSelect label="Category" value={categoryId} onChange={setCategoryId}>
          <option value="">All</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </CatalogFilterSelect>
        <CatalogFilterSelect label="Difficulty" value={difficulty} onChange={setDifficulty}>
          <option value="">All</option>
          <option>Beginner</option>
          <option>Intermediate</option>
          <option>Advanced</option>
        </CatalogFilterSelect>
        <CatalogFilterSelect label="Rating" value={minRating} onChange={setMinRating}>
          <option value="">Any rating</option>
          <option value="4">4+ stars</option>
          <option value="3">3+ stars</option>
          <option value="2">2+ stars</option>
          <option value="1">1+ stars</option>
        </CatalogFilterSelect>
      </CatalogFilterLayout>
    </div>
    </div>
  );
};

export default CoursesPage;
